import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { AvatarService } from 'src/app/avatar/avatar.service';
import { getAssetUrl } from 'src/app/shared/asset';
import { UserInfo } from '../../user-info';
import { AvatarPart, AvatarSlot } from '../tribe';
import { UserService } from '../user.service';

@Injectable()
export class SelectAvatarService {
  private userService = inject(UserService);
  private avatarService = inject(AvatarService);
  private router = inject(Router);

  userInfoSubject = new BehaviorSubject<UserInfo>(undefined);

  avatarSlots: AvatarSlot[] = [];
  selectedParts = new Map<string, string>();
  selectedSlotId: string;
  isAvatarSubmitting = false;
  isBuyingParts = false;

  get avatarPartUrls(): string[] {
    return this.avatarSlots
      .sort((a, b) => (a.priority < b.priority ? -1 : 1))
      .filter((slot) => this.selectedParts.has(slot.id))
      .map((slot) => {
        const part = slot.parts.find((p) => p.id === this.selectedParts.get(slot.id));
        if (part) {
          return getAssetUrl(part.image.path);
        }
      })
      .filter((partUrl) => partUrl !== undefined);
  }

  get selectedSlot(): AvatarSlot | undefined {
    return this.avatarSlots.find((slot) => slot.id == this.selectedSlotId);
  }

  constructor() {
    this.initData();
    this.bindObservable();
  }

  initData() {
    this.userService.getProfile().subscribe((user) => {
      this.userInfoSubject.next(user);
    });
  }

  selectSlot(id: string) {
    this.selectedSlotId = id;
  }

  selectPart(partId: string) {
    this.selectedParts.set(this.selectedSlot.id, partId);
  }

  cancelPart() {
    this.selectedParts.delete(this.selectedSlot.id);
  }

  get unownedSelectedParts(): AvatarPart[] {
    const parts: AvatarPart[] = [];
    for (const [slotId, partId] of this.selectedParts) {
      const part = this.avatarSlots
        .find((slot) => slot.id === slotId)
        .parts.find((p) => p.id === partId);
      if (!part.usable) {
        parts.push(part);
      }
    }
    return parts;
  }

  get totalPrice(): number {
    return this.unownedSelectedParts
      .map((part) => part.price)
      .reduce((prev, curr) => prev + curr, 0);
  }

  get isSubmitDisabled(): boolean {
    return this.totalPrice > 0 || this.missingRequiredSlots.length > 0;
  }

  get missingRequiredSlots(): AvatarSlot[] {
    return this.avatarSlots.filter(
      (slot) =>
        slot.required && !slot.parts.some((part) => part.id === this.selectedParts.get(slot.id))
    );
  }

  buyAvatarParts(): Observable<void> {
    this.isBuyingParts = true;
    const buyingPartIds = this.unownedSelectedParts.map((part) => part.id);
    return this.avatarService.buyParts(buyingPartIds).pipe(
      tap({
        next: () => {
          this.isBuyingParts = false;
          this.updateCharacterParts();
          this.userService.updateCurrentUser();
        },
        error: () => {
          this.isBuyingParts = false;
        }
      })
    );
  }

  submitSelectAvatar() {
    this.isAvatarSubmitting = true;
    const partIds: string[] = [];
    for (const { id: slotId } of this.avatarSlots) {
      if (this.selectedParts.has(slotId)) {
        partIds.push(this.selectedParts.get(slotId));
      }
    }

    this.userService.selectAvatarPart(partIds).subscribe({
      next: () => {
        this.isAvatarSubmitting = false;
        this.userService.updateCurrentUser();
        this.router.navigate(['profile', this.userService.userInfo.username]);
      },
      error: () => {
        this.isAvatarSubmitting = false;
      }
    });
  }

  private bindObservable() {
    this.userInfoSubject.subscribe((userInfo) => {
      if (!userInfo || !userInfo.character) {
        return;
      }
      this.initAvatarPart();
    });
  }

  private initAvatarPart() {
    this.avatarService.getCharacterParts().subscribe((slots) => {
      this.avatarSlots = slots;
      this.setAvatarPart();
      this.initSelectedSlot();
    });
  }

  updateCharacterParts() {
    this.avatarService.getCharacterParts().subscribe((slots) => {
      this.avatarSlots = slots;
    });
  }

  private setAvatarPart() {
    this.avatarService.getUserSelectedParts().subscribe((response) => {
      if (response) {
        for (const slot of this.avatarSlots) {
          const avatarPartId = response[slot.id];
          if (avatarPartId && slot.parts.some((part) => part.id === avatarPartId)) {
            this.selectedParts.set(slot.id, avatarPartId);
          } else if (slot.required && slot.parts.length > 0) {
            this.selectedParts.set(slot.id, slot.parts[0].id);
          }
        }
      }
    });
  }

  private initSelectedSlot() {
    if (this.avatarSlots.length > 0) {
      this.selectedSlotId = this.avatarSlots[0].id;
    }
  }
}
